/**
 * TodayView — Pure presentational UI for the Today priority list.
 * NO fetch, NO navigation, NO side effects.
 * Used by: Today page (with real data from hooks) and landing demo (with mock data).
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { SmartCard } from "@/components/ui/smart-card";
import { TempPill } from "@/components/leads/LeadVisual";
import { Clock } from "lucide-react";

/** Display-only action shape: same as DemoPriorityAction, usable from real PriorityAction via mapper. */
export type TodayViewAction = {
  id: string;
  priority: number;
  stageChip?: "HOT" | "WARM" | "STALE";
  valueLabel?: string;
  title: string;
  whyNow: string;
  overdueLabel?: string;
  riskLabel?: string;
  actions: Array<string>;
};

type TodayViewProps = {
  actions: TodayViewAction[];
  /** 'demo' = no real handlers, compact layout for landing; 'app' = full layout for in-app */
  mode?: "app" | "demo";
  className?: string;
};

function StageChipPill({ stage }: { stage: "HOT" | "WARM" | "STALE" }) {
  const v = stage.toLowerCase() as "hot" | "warm" | "stale";
  return <TempPill value={v} />;
}

export function TodayView({ actions, mode = "app", className }: TodayViewProps) {
  const isDemo = mode === "demo";

  return (
    <div className={cn("space-y-3", isDemo && "p-4 space-y-2", className)}>
      {actions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No actions for today.</p>
      ) : (
        actions.map((action, index) => (
          <SmartCard
            key={action.id}
            spotlight={index === 0}
            glow={index === 0}
            hover
            interactive
            className={cn(
              "group p-4 relative overflow-visible card-shell dsos-card dsos-lead-outline dsos-hover-lift",
              isDemo && "p-3 cursor-default"
            )}
          >
            <div className="flex items-start gap-4 relative z-10">
              {/* Priority number */}
              <div
                className={cn(
                  "w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-200",
                  index === 0
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-secondary text-muted-foreground"
                )}
              >
                {index + 1}
              </div>

              <div className="flex-1 min-w-0 space-y-2.5">
                {/* Header row: stage chip + value */}
                <div className="flex items-center gap-2 flex-wrap">
                  {action.stageChip && <StageChipPill stage={action.stageChip} />}
                  {action.valueLabel && (
                    <span className="text-[11px] font-semibold tabular-nums text-foreground/80 rounded-full border border-border/60 bg-muted/30 px-2.5 py-1">
                      {action.valueLabel}
                    </span>
                  )}
                </div>

                {/* Title / why now */}
                <h4 className="font-semibold text-foreground text-base leading-tight break-words">
                  {action.title}
                </h4>
                <div className="space-y-1.5">
                  <div className="text-[11px] text-foreground/80 font-medium leading-relaxed">
                    <span className="text-foreground/60 font-semibold inline-block mr-1.5">Why now:</span>
                    <span className="inline whitespace-normal">{action.whyNow}</span>
                  </div>
                  {(action.overdueLabel || action.riskLabel) && (
                    <div className="text-[11px] text-foreground/60 leading-relaxed flex items-center gap-1.5">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span className="whitespace-normal">
                        {action.overdueLabel ?? action.riskLabel}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons (visual only in demo; guard blocks interaction) */}
              <div className="flex flex-shrink-0 gap-1.5 flex-wrap justify-end">
                {action.actions.slice(0, 4).map((label) => (
                  <span
                    key={label}
                    className={cn(
                      "inline-flex items-center justify-center rounded-full text-[11px] font-medium px-3 py-1.5 border transition-colors",
                      label === "Done"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted/50 text-muted-foreground border-border/60 hover:bg-muted/80"
                    )}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </SmartCard>
        ))
      )}
    </div>
  );
}
